import { Component, OnInit } from '@angular/core';
import { Patient } from '../../core/models/patient.model';
import { PatientService } from '../../core/services/patient.service';
import { DatePipe } from '@angular/common';
import { NotificationService } from '../../core/services/notification.service';
import { Router } from '@angular/router';
import { User } from '../../core/models/user.model';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { AgePipe } from '../../core/pipes/age.pipe';
import { HasRolePipe } from '../../core/pipes/has-role.pipe';

@Component({
  selector: 'app-patients-view',
  imports: [PortalNavigation, AgePipe, HasRolePipe],
  templateUrl: './patients-view.html',
  styleUrl: './patients-view.scss',
})
export class PatientsView implements OnInit {
  patients: Patient[] = [];
  isLoading = true;
  authService: any;
  patientToDelete: Patient | null = null;
  isDeleting = false;
  userProfile: User | null = null;
  activeRole = 'DOCTOR';

  // Pagination properties
  patientsItemsPerPage = 6;
  patientsCurrentPage = 1;
  patientsPageSize = 0;
  readonly clinicNavigationItems: PortalNavigationItem[] = [
    { label: 'Patients', icon: 'bi-people-fill', route: '/dashboard/patients-view' },
    { label: 'Bookings', icon: 'bi-calendar-check-fill', route: '/dashboard/booking-list' },
    { label: 'Create Booking', icon: 'bi-calendar-plus-fill', route: '/dashboard/create-booking' },
  ];
  readonly managerNavigationItems: PortalNavigationItem[] = [
    { label: 'Reporting', icon: 'bi-bar-chart-line', route: '/dashboard/reporting' },
    { label: 'User Management', icon: 'bi-people', route: '/dashboard/manage-users' },
  ];

  constructor(
    private patientService: PatientService,
    private router: Router,
    private notificationService: NotificationService,
  ) {}

  copiedPatientId: string | null | undefined = null;

  ngOnInit() {
    this.loadUserProfile();
    this.loadPatients();
  }

  copyPatientId(id: string | undefined): void {
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      this.copiedPatientId = id;
      setTimeout(() => {
        this.copiedPatientId = null;
      }, 2000);
    });
  }

  profileCompletionColor(pct: number | undefined): 'danger' | 'warning' | 'success' {
    if (pct == null || pct < 40) return 'danger';
    if (pct < 70) return 'warning';
    return 'success';
  }

  profileCompletionLabel(pct: number | undefined): string {
    if (pct == null || pct < 40) return 'Incomplete';
    if (pct < 70) return 'Partial';
    return 'Complete';
  }

  hasRequiredProfileCompletion(patient: Patient): boolean {
    return (patient.profileCompletionPercentage ?? 0) >= 50;
  }

  profileCompletionAccessMessage(patient: Patient): string {
    return this.hasRequiredProfileCompletion(patient)
      ? ''
      : 'Complete at least 50% of this patient profile to unlock more functionality.';
  }

  loadPatients() {
    this.patientService.getDoctorPatients().subscribe(
      (res) => {
        this.isLoading = false;
        this.patients = res.map((patient) => this.normalizeClinicPatient(patient));
        this.patientsPageSize = Math.ceil(this.patients.length / this.patientsItemsPerPage);
        this.patientsCurrentPage = 1;
      },
      (err) => {
        this.isLoading = false;
        this.notificationService.error(err.error.message);
      },
    );
  }

  viewPatient(id: string) {
    this.router.navigate(['/patient', id]);
  }

  goToCreatePatient() {
    this.router.navigate(['/dashboard/create-patient']);
  }

  get paginatedPatients(): Patient[] {
    const startIndex = (this.patientsCurrentPage - 1) * this.patientsItemsPerPage;
    return this.patients.slice(startIndex, startIndex + this.patientsItemsPerPage);
  }

  patientsPreviousPage(): void {
    if (this.patientsCurrentPage > 1) this.patientsCurrentPage--;
  }

  patientsNextPage(): void {
    if (this.patientsCurrentPage < this.patientsPageSize) this.patientsCurrentPage++;
  }

  goToScheduleAppointment(patient: Patient) {
    if (!this.hasRequiredProfileCompletion(patient)) return;
    this.router.navigate(['/dashboard/create-booking'], { state: { patient } });
  }

  downloadPatientPdf(id: string) {
    const patient = this.patients.find((item) => item.id === id);
    if (!patient || !this.hasRequiredProfileCompletion(patient)) return;
    this.patientService.downloadPatientFile(id).subscribe((res) => {
      const fileURL = window.URL.createObjectURL(res);
      window.open(fileURL); // opens in new tab
    });
  }

  goToCreateMedicalRecord(patient: Patient) {
    if (!this.hasRequiredProfileCompletion(patient)) return;
    this.router.navigate(['/dashboard/patient/medical-file', patient.id], { state: { patient } });
  }

  goToPatientMedicalHistory(patient: Patient) {
    if (patient && patient.id) {
      this.router.navigate(['/dashboard/patient/medical-history', patient.id]);
    }
  }

  goToPatientDetails(patient: Patient) {
    if (!this.hasRequiredProfileCompletion(patient)) return;
    this.router.navigate(['/dashboard/patient/details'], { state: { patient } });
  }

  goToEditPatient(patient: Patient) {
    this.router.navigate(['/dashboard/edit-patient', patient.id]);
  }

  confirmDelete(patient: Patient): void {
    this.patientToDelete = patient;
  }

  deletePatient(): void {
    if (!this.patientToDelete?.id) return;
    this.isDeleting = true;
    this.patientService.deactivatePatient(this.patientToDelete.id).subscribe({
      next: () => {
        this.isDeleting = false;
        const closeBtn = document.getElementById('deleteModalCloseBtn') as HTMLElement;
        closeBtn?.click();
        this.patients = this.patients.filter((p) => p.id !== this.patientToDelete!.id);
        this.patientToDelete = null;
        this.notificationService.error('Patient has been deactivated.');
      },
      error: (err) => {
        this.isDeleting = false;
        this.notificationService.error(err?.error?.message ?? 'Failed to deactivate patient.');
      },
    });
  }

  get portalName(): string {
    return `${this.activeRole.charAt(0)}${this.activeRole.slice(1).toLowerCase()} Portal`;
  }

  get portalIcon(): string {
    return this.activeRole === 'MANAGER'
      ? 'bi-clipboard2-pulse-fill'
      : this.activeRole === 'RECEPTION'
        ? 'bi-hospital-fill'
        : 'bi-heart-pulse-fill';
  }

  get portalClass(): string {
    return this.activeRole === 'MANAGER' ? 'text-danger' : 'text-primary';
  }

  get profileRoute(): string {
    return this.activeRole === 'DOCTOR' ? '/dashboard/doctor-profile' : '/dashboard/profile';
  }

  get primaryNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? [] : this.clinicNavigationItems;
  }

  get additionalNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? this.managerNavigationItems : [];
  }

  private normalizeClinicPatient(patient: Patient): Patient {
    const responsePatient = patient as Patient & {
      medicalAidScheme?: string;
      medicalAidNumber?: string;
      medicalAidPlan?: string;
    };

    if (
      !responsePatient.medicalAidScheme &&
      !responsePatient.medicalAidPlan &&
      !responsePatient.medicalAidNumber
    ) {
      return patient;
    }

    return {
      ...patient,
      clinicPatient: {
        ...patient.clinicPatient,
        medicalAidScheme:
          patient.clinicPatient?.medicalAidScheme ?? responsePatient.medicalAidScheme,
        medicalAidNumber:
          patient.clinicPatient?.medicalAidNumber ?? responsePatient.medicalAidNumber,
        medicalAidPlan: patient.clinicPatient?.medicalAidPlan ?? responsePatient.medicalAidPlan,
      },
    };
  }

  private loadUserProfile(): void {
    const storedProfile = localStorage.getItem('userProfile');
    this.activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';

    if (!storedProfile) {
      return;
    }

    try {
      this.userProfile = JSON.parse(storedProfile) as User;
    } catch {
      this.userProfile = null;
    }
  }
}
